import { Navigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import axios from 'axios'
import { useOnboardingStatus } from '../../hooks/useOnboardingStatus'
import { clearAuth0Storage } from '../../utils/auth0Recovery'
import { Loader2, Mail, RefreshCw, AlertTriangle } from 'lucide-react'

interface OnboardingGuardProps {
  children: React.ReactNode
}

/**
 * Route guard that redirects users to onboarding if they haven't completed registration
 * or to plan selection if they haven't selected a plan yet.
 *
 * For paying users (Active subscription), email verification is not required
 * but a persistent banner is shown prompting them to verify.
 *
 * This should wrap routes that require a fully onboarded user.
 */
export default function OnboardingGuard({ children }: OnboardingGuardProps) {
  const { isLoading, requiresOnboarding, requiresPlanSelection, requiresConsentReacceptance, emailVerified, subscriptionStatus, error, refetch } = useOnboardingStatus()
  const { logout, user } = useAuth0()

  if (isLoading || emailVerified === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  // Block access when onboarding status couldn't be verified due to any error
  // Covers network failures, server errors (500/503/403), and auth errors
  const isStatusError = error && !requiresOnboarding && !requiresPlanSelection && !requiresConsentReacceptance
  if (isStatusError) {
    const isNetworkError = axios.isAxiosError(error) && !error.response
    // Non-Axios errors from /auth/status mean the request never left the
    // interceptor — almost always a dead Auth0 session (stale/rotated refresh
    // token). Retrying the same query will fail identically, so offer a
    // recoverable re-login action instead of a dead-end retry.
    const isSessionError = !axios.isAxiosError(error)
    const handleReLogin = () => {
      // Purge only Auth0 SDK state — keep unrelated persisted UI state
      // (e.g. ProjectContext's current project id) intact.
      clearAuth0Storage()
      logout({ logoutParams: { returnTo: window.location.origin } })
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {isSessionError ? 'Sesja wygasła' : isNetworkError ? 'Błąd połączenia' : 'Błąd serwera'}
          </h1>
          <p className="text-gray-600 mb-6">
            {isSessionError
              ? 'Twoja sesja wygasła lub jest nieprawidłowa. Zaloguj się ponownie, aby kontynuować.'
              : isNetworkError
              ? 'Nie udało się połączyć z serwerem. Sprawdź połączenie internetowe i spróbuj ponownie.'
              : 'Serwer zwrócił nieoczekiwany błąd. Spróbuj ponownie za chwilę.'}
          </p>
          {isSessionError ? (
            <button
              onClick={handleReLogin}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Zaloguj się ponownie
            </button>
          ) : (
            <button
              onClick={() => refetch()}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Spróbuj ponownie
            </button>
          )}
        </div>
      </div>
    )
  }

  // Paying users (Active subscription) can proceed without email verification
  // but will see a banner in the layout
  const isPayingUser = subscriptionStatus === 'Active'

  // Show email verification required screen (only for non-paying users)
  if (emailVerified === false && !isPayingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-6">
            <Mail className="w-8 h-8 text-yellow-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Zweryfikuj swoj adres email
          </h1>
          <p className="text-gray-600 mb-6">
            Wyslalismy link weryfikacyjny na adres{' '}
            <span className="font-medium text-gray-900">{user?.email}</span>.
            Kliknij w link, aby aktywowac konto.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => window.location.reload()}
              className="w-full btn-primary py-3 flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Sprawdz ponownie
            </button>
            <button
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              className="w-full py-3 px-4 rounded-lg font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            >
              Wyloguj sie
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-6">
            Nie otrzymales emaila? Sprawdz folder spam lub{' '}
            <button
              onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              className="text-blue-600 hover:underline"
            >
              zaloguj sie ponownie
            </button>
            , aby wyslac nowy link.
          </p>
        </div>
      </div>
    )
  }

  if (requiresOnboarding) {
    return <Navigate to="/onboarding" replace />
  }

  if (requiresPlanSelection) {
    return <Navigate to="/onboarding/select-plan" replace />
  }

  if (requiresConsentReacceptance) {
    return <Navigate to="/accept-terms" replace />
  }

  return (
    <>
      {/* Email verification banner for paying users who haven't verified yet */}
      {emailVerified === false && isPayingUser && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" />
              <p className="text-sm text-yellow-800">
                Zweryfikuj swój adres email ({user?.email}) w ciągu 7 dni.
                Sprawdź skrzynkę pocztową.
              </p>
            </div>
            <button
              onClick={() => window.location.reload()}
              className="text-sm text-yellow-700 hover:text-yellow-900 font-medium ml-4 whitespace-nowrap"
            >
              Sprawdź ponownie
            </button>
          </div>
        </div>
      )}
      {children}
    </>
  )
}
